(() => {
  const ROOT_SELECTOR = 'product-info[data-yeahbox-mobile-pdp-v3]';
  const MOBILE_QUERY = '(max-width: 749px)';
  const initializedRoots = new WeakSet();

  const getGalleryIndex = (gallery) => {
    const track = gallery?.querySelector('[data-yeahbox-media-track]');
    const total = gallery?.querySelectorAll('[data-yeahbox-media-dot]').length || 0;
    if (!track || !total) return 0;
    return Math.min(
      Math.max(Math.round(track.scrollLeft / Math.max(track.clientWidth, 1)), 0),
      total - 1
    );
  };

  const syncGalleryState = (gallery) => {
    if (!gallery || !window.matchMedia(MOBILE_QUERY).matches) return;
    const dots = Array.from(gallery.querySelectorAll('[data-yeahbox-media-dot]'));
    const total = dots.length || Number(gallery.dataset.yeahboxSlideCount) || 1;
    const index = Math.min(getGalleryIndex(gallery), Math.max(total - 1, 0));
    const current = index + 1;
    const progress = `${(current / Math.max(total, 1)) * 100}%`;

    dots.forEach((dot, dotIndex) => {
      const active = dotIndex === index;
      dot.classList.toggle('is-active', active);
      dot.toggleAttribute('aria-current', active);
    });
    gallery.querySelectorAll('[data-yeahbox-media-current]').forEach((element) => {
      element.textContent = String(current);
    });
    gallery.querySelectorAll('[data-yeahbox-media-total]').forEach((element) => {
      element.textContent = String(total);
    });
    gallery.querySelectorAll('[data-yeahbox-media-progress]').forEach((element) => {
      element.style.width = progress;
      element.setAttribute('role', 'progressbar');
      element.setAttribute('aria-valuemin', '1');
      element.setAttribute('aria-valuemax', String(total));
      element.setAttribute('aria-valuenow', String(current));
    });
    gallery.querySelectorAll('[data-yeahbox-media-step]').forEach((button) => {
      const step = Number(button.dataset.yeahboxMediaStep);
      const disabled = total <= 1 || (step < 0 ? index === 0 : index === total - 1);
      button.disabled = disabled;
      button.classList.toggle('is-disabled', disabled);
      button.setAttribute('aria-disabled', String(disabled));
    });
  };

  const syncButton = (targetBar, sourceBar) => {
    const selector = '.yeahbox-mobile-buy-bar__button--primary';
    const targetButton = targetBar?.querySelector(selector);
    const sourceButton = sourceBar?.querySelector(selector);
    if (!targetButton || !sourceButton) return;

    const disabled = sourceButton.hasAttribute('disabled');
    targetButton.toggleAttribute('disabled', disabled);
    targetButton.setAttribute('aria-disabled', String(disabled));
    const targetLabel = targetButton.querySelector('span');
    const sourceLabel = sourceButton.querySelector('span');
    if (targetLabel && sourceLabel) targetLabel.innerHTML = sourceLabel.innerHTML;
  };

  const createController = (root) => {
    if (initializedRoots.has(root)) return;
    initializedRoots.add(root);

    const sectionId = String(root.dataset.section || '');
    const mobileQuery = window.matchMedia(MOBILE_QUERY);
    const bar = root.querySelector('[data-yeahbox-mobile-buy-bar]');
    const mainSubmit = root.querySelector('.product-form__submit');
    const gallery = root.querySelector('[data-yeahbox-product-media]');
    const galleryTrack = gallery?.querySelector('[data-yeahbox-media-track]');
    let galleryFrame = 0;

    const setBarVisibility = (visible) => {
      bar?.classList.toggle('is-visible', Boolean(visible && mobileQuery.matches));
    };

    const updateBarFromRect = () => {
      if (!mainSubmit || !mobileQuery.matches) {
        setBarVisibility(false);
        return;
      }
      const rect = mainSubmit.getBoundingClientRect();
      const inViewport = rect.top < window.innerHeight && rect.bottom > 0;
      setBarVisibility(!inViewport && rect.bottom <= 0);
    };

    let submitObserver = null;
    if (mainSubmit && 'IntersectionObserver' in window) {
      submitObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry || !mobileQuery.matches) {
            setBarVisibility(false);
            return;
          }
          setBarVisibility(!entry.isIntersecting && entry.boundingClientRect.bottom <= 0);
        },
        { threshold: [0, 0.01, 1] }
      );
      submitObserver.observe(mainSubmit);
    } else {
      window.addEventListener('scroll', updateBarFromRect, { passive: true });
      window.addEventListener('resize', updateBarFromRect, { passive: true });
    }

    const scheduleGallerySync = () => {
      window.cancelAnimationFrame(galleryFrame);
      galleryFrame = window.requestAnimationFrame(() => syncGalleryState(gallery));
    };
    galleryTrack?.addEventListener('scroll', scheduleGallerySync, { passive: true });
    gallery?.addEventListener('click', () => window.setTimeout(scheduleGallerySync, 0));
    const galleryObserver = gallery
      ? new MutationObserver((mutations) => {
          if (
            mutations.some(
              (mutation) =>
                mutation.type === 'attributes' &&
                mutation.target.matches?.('[data-yeahbox-media-dot]')
            )
          ) {
            scheduleGallerySync();
          }
        })
      : null;
    galleryObserver?.observe(gallery, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
    });

    const handleVariantChange = ({ data }) => {
      if (!data || String(data.sectionId) !== sectionId) return;
      const sourceRoot = data.html.getElementById(`MainProduct-${sectionId}`);
      const sourceBar = sourceRoot?.querySelector('[data-yeahbox-mobile-buy-bar]');
      const sourcePurchaseCard = sourceRoot?.querySelector('[data-yeahbox-mobile-purchase-card]');
      const targetPurchaseCard = root.querySelector('[data-yeahbox-mobile-purchase-card]');
      const sourcePrice = data.html.getElementById(`price-${sectionId}`);
      const targetPrice = root.querySelector(`#price-${CSS.escape(sectionId)}`);

      if (sourcePrice && targetPrice) {
        targetPrice.dataset.yeahboxPriceState =
          sourcePrice.dataset.yeahboxPriceState || 'regular';
      }
      const targetBarPrice = bar?.querySelector('.yeahbox-mobile-buy-bar__price');
      const sourceBarPrice = sourceBar?.querySelector('.yeahbox-mobile-buy-bar__price');
      if (targetBarPrice && sourceBarPrice) targetBarPrice.innerHTML = sourceBarPrice.innerHTML;
      if (targetPurchaseCard) {
        targetPurchaseCard.dataset.yeahboxVariantAvailable =
          sourcePurchaseCard?.dataset.yeahboxVariantAvailable || String(Boolean(data.variant?.available));
      }
      syncButton(bar, sourceBar);
      scheduleGallerySync();
    };

    let unsubscribeVariant = null;
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, handleVariantChange);
    }

    const handleMediaChange = () => {
      updateBarFromRect();
      scheduleGallerySync();
    };
    mobileQuery.addEventListener?.('change', handleMediaChange);
    updateBarFromRect();
    syncGalleryState(gallery);

    root.addEventListener(
      'yeahbox:mobile-pdp-destroy',
      () => {
        submitObserver?.disconnect();
        galleryObserver?.disconnect();
        galleryTrack?.removeEventListener('scroll', scheduleGallerySync);
        mobileQuery.removeEventListener?.('change', handleMediaChange);
        if (typeof unsubscribeVariant === 'function') unsubscribeVariant();
      },
      { once: true }
    );
  };

  const refresh = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) createController(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(createController);
  };

  document.addEventListener('shopify:section:load', (event) => refresh(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target
      ?.querySelectorAll?.(ROOT_SELECTOR)
      .forEach((root) => root.dispatchEvent(new CustomEvent('yeahbox:mobile-pdp-destroy')));
  });
  document.addEventListener('product-info:loaded', (event) => refresh(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => refresh(document), { once: true });
  } else {
    refresh(document);
  }
})();
